/**
 * @fileoverview Shared lifecycle policy for inspection case events — the single
 *               rank table and collision merge used by client conflict
 *               resolution and the server stale-write guard.
 * @module sync-config/caseEventLifecycle
 *
 * Responsibilities:
 * - Rank case event statuses so a completed or closed-out event is never
 *   replaced by an earlier lifecycle state (scheduled < inProgress < completed < closedOut).
 * - Merge two copies of one event sharing an eventIndex: the more advanced
 *   lifecycle state is the base; fields only the other copy carries are kept.
 * - Detect a server-side backward transition (terminal existing event whose
 *   incoming counterpart ranks lower — or is absent — at the same eventIndex).
 *
 * Dependencies:
 * - None — pure functions over plain event records.
 *
 * Architecture:
 * - Clock-independent: decisions depend only on event status, never on updatedAt.
 * - Consumed by web/packages/local-db (keyed-array `events` merge) and
 *   api/packages/db (sync push stale-write guard) via `@firesquirrel/sync-config`.
 *
 * Notes:
 * - Unknown or missing statuses rank 0 (least advanced).
 * - Intentional reopening is not modeled here; no reopen operation exists in the codebase.
 */

/**
 * @contract CaseEventStatusRank
 * Purpose: Ordering of event lifecycle statuses; higher means more advanced.
 * Fields: scheduled=0, inProgress=1, completed=2, closedOut=3.
 * Rules: Unknown statuses rank 0. Terminal statuses are completed and closedOut.
 * Sync: Pure constant shared by client and server.
 */
export const CASE_EVENT_STATUS_RANK: Readonly<Record<string, number>> = Object.freeze({
  scheduled: 0,
  inProgress: 1,
  completed: 2,
  closedOut: 3,
});

const TERMINAL_RANK = CASE_EVENT_STATUS_RANK.completed;

/**
 * @process caseEventStatusRank
 * Steps: Look up the status in the rank table; unknown or non-string values rank 0.
 * Inputs: Any event status value.
 * Outputs: Numeric lifecycle rank.
 * Failure Handling: Non-string or unrecognized statuses return 0.
 * Idempotency: REQUIRED — pure lookup.
 */
export function caseEventStatusRank(status: unknown): number {
  if (typeof status !== 'string') return 0;
  return Object.prototype.hasOwnProperty.call(CASE_EVENT_STATUS_RANK, status)
    ? CASE_EVENT_STATUS_RANK[status]
    : 0;
}

/**
 * @process caseStatusRank
 * Steps: Map a top-level case status string to its lifecycle rank.
 * Inputs: Any case status value.
 * Outputs: Numeric rank (0 for unknown).
 * Failure Handling: Non-string or unrecognized statuses return 0.
 * Idempotency: REQUIRED — pure lookup.
 *
 * Ranks are ordered so that completion (caseCompleted) outranks active work,
 * and closedOut outranks everything as the terminal state.
 */
export function caseStatusRank(status: unknown): number {
  if (typeof status !== 'string') return 0;
  switch (status) {
    case 'notStarted': return 1;
    case 'active': return 2;
    case 'caseCompleted': return 3;
    case 'closedOut': return 4;
    default: return 0;
  }
}

/**
 * @process mergeCaseEventCollision
 * Steps: Pick the higher-ranked event as base (local wins ties); copy keys only the other event carries.
 * Inputs: Local event and server event sharing an eventIndex.
 * Outputs: Merged event object; never a lower-ranked status than either input.
 * Failure Handling: Equal ranks keep the local copy and fill missing fields from the server copy.
 * Idempotency: REQUIRED — pure derivation.
 */
export function mergeCaseEventCollision(localEvent: Record<string, any>, serverEvent: Record<string, any>): Record<string, any> {
  const localWins = caseEventStatusRank(localEvent.status) >= caseEventStatusRank(serverEvent.status);
  return localWins
    ? { ...serverEvent, ...localEvent }
    : { ...localEvent, ...serverEvent };
}

/**
 * @contract BackwardCaseEventTransition
 * Purpose: Describes the first event whose incoming status regresses from a terminal existing status.
 * Fields: eventIndex — the shared event index where the regression was detected.
 * Rules: Only emitted when the existing event is completed or closedOut and the incoming event ranks
 *        lower or carries no event at that index (omission is removal under whole-array $set).
 * Sync: Server-side verdict; never persisted.
 */
export interface BackwardCaseEventTransition {
  eventIndex: number;
}

/**
 * @process findBackwardCaseEventTransition
 * Steps: Index incoming events by eventIndex; find the first terminal existing event whose incoming
 *        counterpart ranks lower OR is absent. The push update path writes the incoming `events`
 *        array wholesale via $set, so a terminal event missing from a stale client snapshot would
 *        be erased without this check. An absent incoming `events` field itself is NOT flagged —
 *        $set never unsets fields the client did not send.
 * Inputs: Current server case document and the incoming (pushed) case document.
 * Outputs: The regression descriptor or undefined.
 * Failure Handling: Missing or non-array events yield undefined; events without an eventIndex are ignored.
 * Idempotency: REQUIRED — pure derivation.
 */
export function findBackwardCaseEventTransition(
  existing: { events?: unknown } | null | undefined,
  incoming: { events?: unknown } | null | undefined,
): BackwardCaseEventTransition | undefined {
  if (!Array.isArray(existing?.events) || !Array.isArray(incoming?.events)) return undefined;

  const incomingByIndex = new Map<number, Record<string, any>>();
  for (let i = 0; i < (incoming.events as Array<Record<string, any>>).length; i++) {
    const event = incoming.events[i];
    const idx = typeof event?.eventIndex === 'number' ? event.eventIndex : i;
    incomingByIndex.set(idx, event);
  }

  for (let i = 0; i < (existing.events as Array<Record<string, any>>).length; i++) {
    const event = existing.events[i];
    const idx = typeof event?.eventIndex === 'number' ? event.eventIndex : i;
    const existingRank = caseEventStatusRank(event.status);
    if (existingRank < TERMINAL_RANK) continue;
    const incomingEvent = incomingByIndex.get(idx);
    if (!incomingEvent || caseEventStatusRank(incomingEvent.status) < existingRank) {
      return { eventIndex: idx };
    }
  }
  return undefined;
}

/**
 * @process isBackwardCaseEventTransition
 * Steps: Delegate to findBackwardCaseEventTransition and report presence.
 * Inputs: Current server case document and the incoming (pushed) case document.
 * Outputs: true when the push would revert a completed or closedOut event.
 * Failure Handling: Malformed inputs return false (no rejection).
 * Idempotency: REQUIRED — pure predicate.
 */
export function isBackwardCaseEventTransition(
  existing: { events?: unknown } | null | undefined,
  incoming: { events?: unknown } | null | undefined,
): boolean {
  return findBackwardCaseEventTransition(existing, incoming) !== undefined;
}
